import { useParams, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { ServiceHero } from "@/components/ServiceHero";
import { RecentProjects } from "@/components/RecentProjects";
import { TrustBadges } from "@/components/TrustBadges";
import { CallToActionSection } from "@/components/CallToActionSection";
import { ServicesList } from "@/components/ServicesList";
import { Testimonials } from "@/components/Testimonials";
import { FAQ } from "@/components/FAQ";
import { Coupons } from "@/components/Coupons";
import { ProjectGallery } from "@/components/ProjectGallery";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { usePhoneNumber } from "@/hooks/usePhoneNumber";
import { usePhoneBar } from "@/contexts/PhoneBarContext";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface Service {
  id: string;
  slug: string;
  name: string;
  description: string;
  hero_title_template: string;
  hero_subtitle_template: string;
  service_list: string[];
  is_emergency: boolean;
  meta_title_template: string;
  meta_description_template: string;
}

const ServiceLanding = () => {
  const { serviceSlug, cityName } = useParams<{ serviceSlug: string; cityName?: string }>();
  const { setPhoneNumber } = usePhoneBar();
  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Format city name - default to "Long Island" if no city specified
  const formatCityName = (city: string) => {
    return city
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formattedCity = cityName ? formatCityName(cityName) : "Long Island";
  const phoneInfo = usePhoneNumber(cityName ? formattedCity : undefined);

  useEffect(() => {
    setPhoneNumber(phoneInfo.tel);
  }, [phoneInfo.tel, setPhoneNumber]);

  // Fetch service data
  useEffect(() => {
    const fetchService = async () => {
      if (!serviceSlug) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('services')
          .select('*')
          .eq('slug', serviceSlug)
          .single();

        if (error || !data) {
          setNotFound(true);
        } else {
          setService(data);
        }
      } catch (err) {
        console.error('Error fetching service:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [serviceSlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (notFound || !service) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className="min-h-screen">
      <SEOHead 
        cityName={formattedCity}
        title={service.meta_title_template?.replace('{city}', formattedCity)}
        description={service.meta_description_template?.replace('{city}', formattedCity)}
        canonical={cityName 
          ? `https://quote.safewayroofingandchimney.com/${serviceSlug}/${cityName}`
          : `https://quote.safewayroofingandchimney.com/${serviceSlug}`
        }
      />
      <Header />
      <ServiceHero service={service} cityName={formattedCity} />
      <RecentProjects cityName={formattedCity} serviceSlug={serviceSlug} />
      <TrustBadges cityName={formattedCity} />
      <CallToActionSection />
      <ServicesList service={service} cityName={formattedCity} />
      <Testimonials />
      <FAQ cityName={formattedCity} />
      <Coupons />
      <ProjectGallery serviceSlug={serviceSlug} />
      <Footer />
    </main>
  );
};

export default ServiceLanding;
